<template>
  <div v-if="selectedPartInspection" class="mt-4">
    <EmptyEmailDialog
      :visible="emptyEmailDialog.show"
      :person="emptyEmailDialog.person"
      @confirm="onConfirmAddWithEmptyEmail"
      @cancel="onCancelAddWithEmptyEmail"
      @hide="onHideEmptyEmailDialog"
    />

    <div class="mb-4 d-flex flex-row align-items-center">
      <div>{{ $t('Participants') }}</div>
      <b-icon
        :icon="isExpanded ? 'caret-down-fill' : 'caret-right-fill'"
        class="cursor-pointer ml-2"
        @click="toggleList"
      />
      <div class="flex-grow-1" />
      <div v-if="!disabled" class="cursor-pointer toggle-all mr-2" @click="onToggleAllClick">
        {{ areAllSelected ? $t('Select none') : $t('Select all') }}
      </div>
      <div>{{ `${selectedCount} / ${participants.length}` }}</div>
    </div>

    <div :class="{ 'participants-list-collapsed': !isExpanded }">
      <div v-for="(participant, index) in participants" :key="index">
        <person-badge :person="participant">
          <b-custom-switch
            :value="participant.participates"
            label=""
            :disabled="disabled"
            @input="toggleParticipation(participant, $event)"
          />
        </person-badge>
        <hr />
      </div>

      <multiselect
        v-if="!disabled && hasSimplePermissions('saveInspectionProperties')"
        :options="options"
        :custom-label="getItemText"
        group-label="label"
        group-values="persons"
        track-by="_id"
        :multiple="false"
        :placeholder="$t('Add Participant')"
        class="boon-multiselect mt-4"
        @input="onParticipantSelect"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';

import PersonBadge from '@/components/toolbar/PersonBadge';
import BCustomSwitch from '@/components/organisation/BCustomSwitch';
import EmptyEmailDialog from '@/components/projectplan/EmptyEmailDialog';

export default {
  name: 'PartEditInspectionParticipants',

  components: {
    Multiselect,
    PersonBadge,
    BCustomSwitch,
    EmptyEmailDialog,
  },

  props: {
    disabled: {
      type: Boolean,
      default: null,
    },
  },

  data() {
    return {
      isExpanded: false,
      emptyEmailDialog: {
        show: false,
        person: null,
      },
    };
  },

  computed: {
    ...mapGetters(['selectedPartInspection', 'hasSimplePermissions', 'groupedPersons', 'personCompanyName']),

    participants() {
      return this.selectedPartInspection ? this.selectedPartInspection.participants : [];
    },

    options() {
      return this.groupedPersons().map(({ _id, companyName, persons }) => {
        const items = [];
        if (persons) {
          persons.forEach((person) => {
            if (this.participants.every(({ personId }) => personId !== person._id)) {
              const fullName = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
              items.push({
                _id: person._id,
                firstName: person.firstName,
                lastName: person.lastName,
                email: person.email,
                companyId: person.companyId,
                label: companyName ? [fullName, `(${companyName})`].filter(Boolean).join(' ').trim() : fullName,
              });
            }
          });
        }

        return {
          _id,
          label: companyName,
          persons: items,
        };
      });
    },

    selectedCount() {
      let count = 0;
      this.participants.forEach(({ participates }) => (count += participates ? 1 : 0));
      return count;
    },

    areAllSelected() {
      return this.selectedCount >= this.participants.length;
    },
  },

  methods: {
    ...mapActions(['addInspectionParticipant', 'updateInspectionParticipantSelection']),

    toggleList() {
      this.isExpanded = !this.isExpanded;
    },

    getItemText(item) {
      return this.personCompanyName(item._id);
    },

    addParticipant(person) {
      this.addInspectionParticipant({
        updateInspection: this.selectedPartInspection,
        person,
      });
    },

    toggleParticipation(participant, selected) {
      if (this.disabled) {
        return;
      }

      this.updateInspectionParticipantSelection({
        updateInspection: this.selectedPartInspection,
        selection: [{ personId: participant.personId, selected }],
        reason: `Updated participant ${participant.personId}: set participates to ${selected}`,
      });
    },

    onToggleAllClick() {
      if (this.disabled) {
        return;
      }

      const selected = !this.areAllSelected;
      this.updateInspectionParticipantSelection({
        updateInspection: this.selectedPartInspection,
        selection: this.participants.map(({ personId }) => ({ personId, selected })),
        reason: `Updated all participants: set participates to ${selected}`,
      });
    },

    onParticipantSelect(person) {
      if (person.email && person.email.trim()) {
        this.addParticipant(person);
      } else {
        this.emptyEmailDialog = { show: true, person };
      }
    },

    closeEmptyEmailDialog() {
      this.emptyEmailDialog = { show: false, person: null };
    },

    onConfirmAddWithEmptyEmail() {
      this.addParticipant(this.emptyEmailDialog.person);
      this.closeEmptyEmailDialog();
    },

    onCancelAddWithEmptyEmail() {
      this.closeEmptyEmailDialog();
    },

    onHideEmptyEmailDialog() {
      this.closeEmptyEmailDialog();
    },
  },
};
</script>

<style scoped>
hr {
  margin: 2mm 0mm 2mm 15mm;
}

.participants-list-collapsed {
  display: none;
}

.toggle-all {
  color: var(--bs-primary);
}
</style>



// WEBPACK FOOTER //
// src/components/projectplan/PartEditInspectionParticipants.vue